import React, { useCallback, useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { TextButton } from '@xingternal/button';
import { useViewerData } from '@xingternal/hub';
import { IconArrowLeft } from '@xingternal/icons';
import { Column, Row } from '@xingternal/xing-grid';

import { DEFAULT_COUNTRY_CODE } from '@/domain/job-application/constants';
import { trackSubmitValidationErrors } from '@/domain/job-application/helpers';
import { useBreakpoints, useLoginStates, useTrackNwtViewedScreen } from '@/hooks';

import {
  ApplyFormSetFieldValueProvider,
  useFormSettingsTrackingData,
  useFormUploadsState,
  useSetApplicationCvFileIdValue,
  useSetApplicationEmailValue,
  useSetApplicationFullName,
  useUpdatePrefilledFields,
} from '../../contexts';
import {
  useApplyFormSubmit,
  useIsLoggedOutProfileCreationEnabled,
  useTrackApplyFormPageview,
  useUserContactDetails,
} from '../../hooks';
import { LOFormFieldValues, XingApplicationJob } from '../../types';
import { ApplyFormHeading } from '../apply-form-heading';
import { DirtyFormCloseDialog } from '../dirty-form-close-dialog';
import { LoggedInApplyForm } from '../logged-in-apply-form';
import { LoggedOutApplyForm } from '../logged-out-apply-form';
import { PrivacyPolicyLinks } from '../privacy-policy-links';

import * as S from '@/domain/job-application/job-application.common.styles';

type Props = {
  job: XingApplicationJob;
  showConfirmationStep: () => void;
  showErrorStep: () => void;
};

const APPLY_EXIT_ORIGIN_SEARCH = '?sc_o=jobs_apply_exit';

export const ApplyForm: React.VFC<Props> = ({ job, showConfirmationStep, showErrorStep }) => {
  const { isLoggedIn } = useLoginStates();
  const history = useHistory();
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const { isUploading, showPendingUploadError } = useFormUploadsState();
  const { isJobDeactivated, isSubmitInProgress, submitFormData } = useApplyFormSubmit({
    jobGlobalId: job.globalId,
    jobId: job.id,
    jobSlug: job.slug,
    onSubmitSuccess: showConfirmationStep,
    onBadRequestError: showErrorStep,
  });
  const { data } = useViewerData();
  const userId = data?.user?.id;
  const { isMobile } = useBreakpoints();
  const isLoggedOutProfileCreationEnabled = useIsLoggedOutProfileCreationEnabled();

  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      ...(!isLoggedIn && {
        firstName: '',
        lastName: '',
        enterCvMode: 'upload',
        cvData: {
          education: {
            startDate: '',
            endDate: '',
            isPresentEndDate: false,
            degree: '',
            institution: '',
          },
          overallExperience: '',
          careerStage: {
            startDate: '',
            endDate: '',
            isPresentEndDate: false,
            position: '',
            employer: '',
          },
        },
      }),
      email: '',
      countryCode: DEFAULT_COUNTRY_CODE,
      phone: '',
      cvDoc: '',
      otherDocs: '',
      comments: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const updateUploadDocsHandler = useCallback(
    (value: string, field: 'cvDoc' | 'otherDocs'): void => {
      setValue(field, value, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  const { updatePrefilledField } = useUpdatePrefilledFields();

  const updateUserContactDataField = useCallback(
    (value: string, field: 'email' | 'phone' | 'countryCode'): void => {
      setValue(field, value, { shouldValidate: false, shouldDirty: false });
      updatePrefilledField(field);
    },
    [setValue, updatePrefilledField]
  );

  const onSubmitError = (submitFieldErrors): void => {
    trackSubmitValidationErrors({ submitFieldErrors, jobGlobalId: job.globalId, userId, isMobile });
  };

  const setApplicationEmailValue = useSetApplicationEmailValue();
  const setApplicationCvFileIdValue = useSetApplicationCvFileIdValue();
  const setApplicationFullName = useSetApplicationFullName();

  const onSubmit = (): void => {
    if (isUploading) {
      showPendingUploadError();
    } else {
      if (isLoggedIn) {
        const [uploadedCvFileId] = getValues(['cvDoc']);
        setApplicationCvFileIdValue(uploadedCvFileId);
      }

      if (isLoggedIn || isLoggedOutProfileCreationEnabled) {
        const [enteredEmailAddress] = getValues(['email']);
        setApplicationEmailValue(enteredEmailAddress);
      }

      if (isLoggedOutProfileCreationEnabled) {
        const [firstName, lastName] = getValues(['firstName', 'lastName']);
        setApplicationFullName({ firstName: firstName, lastName: lastName });
      }

      handleSubmit(submitFormData, onSubmitError)();
    }
  };

  const backToJobPage = useCallback(
    (): void => history.replace(`/jobs${isJobDeactivated ? '' : `/${job.slug}`}${APPLY_EXIT_ORIGIN_SEARCH}`),
    [history, isJobDeactivated, job.slug]
  );

  const onBackButtonClick = useCallback((): void => {
    isDirty ? setShowCloseDialog(true) : backToJobPage();
  }, [backToJobPage, isDirty]);

  const formSettingsTrackingProp = useFormSettingsTrackingData();

  useTrackNwtViewedScreen({
    page: 'jobs/apply',
    context: 'instant_apply',
    item_urn: job.globalId,
    flags: formSettingsTrackingProp,
  });
  useTrackApplyFormPageview();
  useUserContactDetails(updateUserContactDataField, userId);

  useEffect(() => {
    return () => {
      const { origin, pathname, search } = window.location;

      if (!pathname.includes('/application') && search !== APPLY_EXIT_ORIGIN_SEARCH) {
        // includes apply exit origin param in case of navigation back
        window.history.replaceState({}, '', `${origin}${pathname}${APPLY_EXIT_ORIGIN_SEARCH}`);
      }
    };
  }, []);

  return (
    <>
      <Row>
        <Column size={12} sizeDesktop={8} sizeTablet={10} offsetDesktop={2} offsetTablet={1}>
          <S.BackButton icon={IconArrowLeft} onClick={onBackButtonClick}>
            <FormattedMessage id="JOBS_POSTING_EASY_APPLY_FORM_BACK_HEADER_BUTTON" />
          </S.BackButton>
          <ApplyFormHeading company={job.companyInfo.companyNameOverride} />
          <S.Fieldset disabled={isSubmitInProgress}>
            {isLoggedIn ? (
              <LoggedInApplyForm control={control} updateUploadDocsHandler={updateUploadDocsHandler} />
            ) : (
              <ApplyFormSetFieldValueProvider setFieldValue={setValue}>
                <LoggedOutApplyForm
                  control={control as Control<LOFormFieldValues>}
                  updateUploadDocsHandler={updateUploadDocsHandler}
                />
              </ApplyFormSetFieldValueProvider>
            )}
          </S.Fieldset>
          <S.PrivacySection>
            <PrivacyPolicyLinks
              externalDocuments={job.application.formExternalDocuments?.externalDocuments}
              replySettings={job.application.replySettings}
            />
          </S.PrivacySection>
          {isLoggedOutProfileCreationEnabled ? (
            <S.CreateProfileConsent>
              <FormattedMessage id="JOBS_INSTANT_APPLY_CREATE_PROFILE_CONSENT_WITH_CV_DATA" />
            </S.CreateProfileConsent>
          ) : null}
          <S.ButtonsSection>
            <S.SubmitButton
              variant="primary"
              size="medium"
              onClick={onSubmit}
              loading={isSubmitInProgress}
              data-cy="instant-apply-confirm-button"
            >
              <FormattedMessage id="JOBS_POSTING_EASY_APPLY_FORM_CONFIRM_BUTTON" />
            </S.SubmitButton>
            <TextButton size="medium" onClick={onBackButtonClick} data-cy="instant-apply-cancel-button">
              <FormattedMessage id="JOBS_POSTING_EASY_APPLY_FORM_CANCEL_BUTTON" />
            </TextButton>
          </S.ButtonsSection>
        </Column>
      </Row>
      <DirtyFormCloseDialog
        show={showCloseDialog}
        onCancel={() => setShowCloseDialog(false)}
        onConfirm={() => backToJobPage()}
      />
    </>
  );
};
